import { CONDITION_LABELS, publicFields, type Item, type ItemCondition } from "./types.js";
import { priceFromCents } from "./validate.js";

/**
 * Stage 4: an item turned into text somebody pastes into a marketplace form.
 *
 * **This is the second place private data could leave the app**, and the one a
 * stranger reads. So, as with the prompt builder, what goes into a listing is
 * decided in one function, `buildListing()`, from a short list of things that
 * are public by decision: title, description, condition, price, category, the
 * fields that pass `publicFields()`, and the pickup text. It never reads
 * `notes`, a private field, the tags, or the item's place in the location tree
 * -- "Basement > Shelf A > Bin 3" is where somebody's things are kept, and it
 * is not a pickup address. Pickup is a *field* the person writes on purpose.
 *
 * Pure, and independent of the prompt builder: a later LLM-assisted listing
 * goes through the Stage 3 handoff and lands in the item, not in here.
 */

export type ListingFormat = "social" | "classifieds";

export const LISTING_FORMATS: readonly ListingFormat[] = ["social", "classifieds"];

export const LISTING_FORMAT_LABELS: Record<ListingFormat, string> = {
  social: "Social marketplace",
  classifieds: "Classifieds",
};

/**
 * What each kind of platform is known to cut off. These are the commonly hit
 * limits at the time of writing, not an API contract; a warning that is a
 * little stale costs a moment, so they are constants to edit rather than to
 * discover. A classifieds body allows far more than an inventory description
 * reaches, so only the social format has a description limit here.
 */
export const LISTING_LIMITS: Record<ListingFormat, { title: number; description: number | null }> = {
  social: { title: 150, description: 5000 },
  classifieds: { title: 70, description: null },
};

/** The labels a public field may carry to be read as the pickup place. Case does not matter. */
const PICKUP_LABELS = ["pickup", "pickup location", "pickup area", "pick up", "pick-up"];

export const isPickupField = (label: string): boolean => PICKUP_LABELS.includes(label.trim().toLowerCase());

/** How each format words a condition. Lossy on purpose: neither has our six-way scale. */
const CONDITION_WORDS: Record<ListingFormat, Record<Exclude<ItemCondition, "">, string>> = {
  social: {
    new: "New",
    like_new: "Used - Like New",
    good: "Used - Good",
    fair: "Used - Fair",
    poor: "Used - Fair",
    for_parts: "Used - Fair",
  },
  classifieds: {
    new: "new",
    like_new: "like new",
    good: "good",
    fair: "fair",
    poor: "fair",
    for_parts: "salvage",
  },
};

/** One thing to paste into one box on the platform's form. */
export interface ListingPart {
  key: "title" | "price" | "category" | "condition" | "description" | "location";
  /** What the platform's own form calls the box, so a person can find it. */
  label: string;
  value: string;
  /** The platform's limit for this box, when it has one worth warning about. */
  limit: number | null;
}

export interface Listing {
  format: ListingFormat;
  parts: ListingPart[];
  /** Every filled part as "Label: value", for one paste into a notes app or a message. */
  text: string;
  /** Sentences a person can act on, in the order they matter. Never blocks copying. */
  warnings: string[];
}

export interface ListingSource {
  item: Item;
  /** The category's name, or null. The id means nothing to the platform it's pasted into. */
  categoryName: string | null;
}

/** "25" for whole dollars, "25.50" otherwise: what a price box on either platform accepts. */
export function listingPrice(cents: number | null): string {
  if (cents === null) return "";
  const dollars = priceFromCents(cents);
  return dollars.endsWith(".00") ? dollars.slice(0, -3) : dollars;
}

/** The description as posted: what was written, then the public details as plain lines. */
export function listingDescription(item: Item): string {
  const details = publicFields(item.fields)
    .filter((field) => !isPickupField(field.label) && field.label.trim() && field.value.trim())
    .map((field) => `${field.label.trim()}: ${field.value.trim()}`);

  const blocks: string[] = [];
  if (item.description.trim()) blocks.push(item.description.trim());
  if (details.length > 0) blocks.push(details.join("\n"));

  // "Poor" and "for parts" both land on the nearest thing a platform has, and
  // the buyer should still be told the truth.
  if (item.condition === "poor" || item.condition === "for_parts") {
    blocks.push(`Condition: ${CONDITION_LABELS[item.condition]}`);
  }
  if (item.quantity > 1) blocks.push(`Quantity available: ${item.quantity}`);

  return blocks.join("\n\n");
}

export function buildListing({ item, categoryName }: ListingSource, format: ListingFormat): Listing {
  const limits = LISTING_LIMITS[format];
  const title = item.title.trim();
  const description = listingDescription(item);
  const pickup =
    publicFields(item.fields)
      .find((field) => isPickupField(field.label) && field.value.trim())
      ?.value.trim() ?? "";
  const condition = item.condition ? CONDITION_WORDS[format][item.condition] : "";
  const price = listingPrice(item.priceCents);

  const parts: ListingPart[] =
    format === "social"
      ? [
          { key: "title", label: "Title", value: title, limit: limits.title },
          { key: "price", label: "Price", value: price, limit: null },
          { key: "category", label: "Category", value: categoryName ?? "", limit: null },
          { key: "condition", label: "Condition", value: condition, limit: null },
          { key: "description", label: "Description", value: description, limit: limits.description },
          { key: "location", label: "Location", value: pickup, limit: null },
        ]
      : [
          { key: "title", label: "Posting title", value: title, limit: limits.title },
          { key: "price", label: "Price", value: price, limit: null },
          { key: "location", label: "Specific location", value: pickup, limit: null },
          { key: "condition", label: "Condition", value: condition, limit: null },
          { key: "description", label: "Posting body", value: description, limit: limits.description },
        ];

  const platform = LISTING_FORMAT_LABELS[format];
  const warnings: string[] = [];
  if (!title) warnings.push("This item has no title, and every listing needs one.");
  else if (title.length > limits.title) {
    warnings.push(
      `The title is ${title.length} characters and ${platform} allows ${limits.title}. Shorten it before posting.`,
    );
  }
  if (item.priceCents === null) warnings.push("No price yet. Set one on the item, or type it on the form.");
  if (!item.condition) warnings.push("No condition yet. Buyers filter on it.");
  if (!description) warnings.push("No description, and nothing public to put in its place.");
  else if (limits.description !== null && description.length > limits.description) {
    warnings.push(
      `The description is ${description.length} characters and ${platform} allows ${limits.description}.`,
    );
  }
  if (item.photos.length === 0) warnings.push("No photos. Listings without them are mostly ignored.");
  if (!pickup) {
    warnings.push('No pickup place. Add a public field labelled "Pickup" if you want it filled in here.');
  }

  const text = parts
    .filter((part) => part.value)
    .map((part) =>
      part.key === "description" ? `${part.label}:\n${part.value}` : `${part.label}: ${part.value}`,
    )
    .join("\n\n");

  return { format, parts, text, warnings };
}

/** How many details a listing left out on purpose, so the screen can say so. */
export function withheldCount(item: Item): number {
  return item.fields.filter((field) => field.visibility === "private").length + (item.notes.trim() ? 1 : 0);
}
